import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Ruler, Shirt, ShoppingBag, ArrowRight, Camera, Wand2, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const heroImages = [
  { src: "https://images.unsplash.com/photo-1552252059-9d77e4059ad1?crop=entropy&cs=srgb&fm=jpg&q=85&w=500", alt: "Ragazzo look casual camicia bianca e jeans", cls: "top-0 left-0 w-40 sm:w-52 h-56 sm:h-72 rotate-[-6deg] z-20" },
  { src: "https://images.unsplash.com/photo-1600219823854-f3bfee26ea9c?crop=entropy&cs=srgb&fm=jpg&q=85&w=500", alt: "Ragazza in jeans e camicia bianca", cls: "top-6 right-0 w-36 sm:w-48 h-52 sm:h-64 rotate-[6deg] z-20" },
  { src: "https://images.unsplash.com/photo-1516257984-b1b4d707412e?crop=entropy&cs=srgb&fm=jpg&q=85&w=500", alt: "Ragazzo con giacca di jeans", cls: "top-48 sm:top-60 left-4 w-36 sm:w-44 h-48 sm:h-56 rotate-[5deg] z-30" },
  { src: "https://images.unsplash.com/photo-1534404483017-8743b4e935cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=500", alt: "Ragazza con giacca arancione", cls: "top-52 sm:top-64 right-2 w-36 sm:w-44 h-48 sm:h-56 rotate-[-5deg] z-20" },
  { src: "https://images.unsplash.com/photo-1617114919297-3c8ddb01f599?crop=entropy&cs=srgb&fm=jpg&q=85&w=500", alt: "Ragazzo con giacca cammello e jeans", cls: "top-24 sm:top-28 left-1/2 -translate-x-1/2 w-36 sm:w-44 h-52 sm:h-64 rotate-[-3deg] z-40" },
];

const steps = [
  { icon: Camera, title: "Carica le tue foto", desc: "Scatta o carica foto a figura intera da più angolazioni: frontale, laterale e posteriore." },
  { icon: ScanLine, title: "Analisi della corporatura", desc: "L'IA studia le proporzioni geometriche e definisce con precisione la tua corporatura." },
  { icon: Wand2, title: "Outfit su misura", desc: "Stabilisci il tuo budget di spesa, al resto pensiamo noi… Scegli stile e occasione e lo stylist IA compone e visualizza il look su di te." },
  { icon: ShoppingBag, title: "Ordina i capi", desc: "Ricevi la lista completa con taglie consigliate e link diretti ai negozi online." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1e5fe0] via-[#2f74f0] to-[#1a4fc0]">
        <div className="absolute inset-0 opacity-[0.15] bg-[radial-gradient(circle_at_20%_20%,white,transparent_45%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/90 border border-white/40 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="h-3.5 w-3.5" /> Il tuo stylista personale con IA
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
              L'eleganza <span className="text-[#f5c86b] italic">su misura</span>, generata per te.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-xl">
              My Look analizza la tua corporatura dalle foto e compone outfit completi — dalle scarpe ai
              gioielli — realmente in commercio, nel rispetto del tuo budget. Precisione da sartoria, potenza dell'intelligenza artificiale.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                data-testid="hero-cta-start"
                onClick={() => navigate(user ? "/studio" : "/auth")}
                className="bg-white hover:bg-white/90 text-[#1e5fe0] font-semibold rounded-full px-7 h-12 text-base shadow-lg"
              >
                Inizia ora <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                data-testid="hero-cta-how"
                variant="outline"
                onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-full px-7 h-12 text-base border-white/50 text-white hover:bg-white/10 hover:text-white bg-transparent"
              >
                Come funziona
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative h-[440px] sm:h-[540px]">
              {heroImages.map((im, i) => (
                <motion.img
                  key={im.src}
                  src={im.src}
                  alt={im.alt}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className={`absolute object-cover rounded-2xl border-4 border-white shadow-2xl ${im.cls}`}
                />
              ))}
            </div>
            <div className="absolute -bottom-3 right-2 backdrop-blur-xl bg-white/95 border border-white/60 rounded-full px-4 py-2 flex items-center gap-2 text-sm text-[#1e5fe0] z-50 shadow-lg">
              <Ruler className="h-4 w-4 text-[#2f74f0]" /> Ogni stile, ogni dettaglio
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">Il processo</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-[#3a2a1c]">Quattro passi verso il tuo look</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-lg border border-[#1e5fe0]/20 bg-white/75 p-6 hover:border-[#2f74f0]/50 transition-colors"
            >
              <span className="grid place-items-center h-11 w-11 rounded-md bg-[#1e5fe0]/10 border border-[#1e5fe0]/30 mb-4">
                <s.icon className="h-5 w-5 text-[#2f74f0]" />
              </span>
              <div className="font-mono text-xs text-[#a9772f] mb-2">0{i + 1}</div>
              <h3 className="font-serif text-xl text-[#2e2015] mb-2">{s.title}</h3>
              <p className="text-sm text-[#6f5c47] leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-2xl border border-[#1e5fe0]/25 bg-gradient-to-br from-white/80 to-[#c4d6e4] p-10 lg:p-16 text-center relative overflow-hidden">
          <Shirt className="absolute -right-6 -top-6 h-40 w-40 text-[#a9772f]/10" />
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2e2015]">Pronto a vestire meglio?</h2>
          <p className="mt-4 text-[#6f5c47] max-w-xl mx-auto">
            Crea il tuo profilo, carica le foto e lascia che l'atelier digitale faccia il resto.
          </p>
          <Button
            data-testid="cta-bottom-start"
            onClick={() => navigate(user ? "/studio" : "/auth")}
            className="mt-8 bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold rounded-full px-8 h-12 text-base"
          >
            Crea il mio outfit <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
